import { companyApi, CompanyMonitorPageReq, CompanyMonitorPageRow, CompanyStatus, OrderItem } from '@/axios/company';
import CommonTable from '@/components/Table';
import SearchInput from '@/components/SearchInput';
import { useRequest, useSetState } from 'ahooks';
import { Button, Flex, InputNumber, Select, Space, Typography } from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import type { FilterDropdownProps, SorterResult } from 'antd/es/table/interface';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './index.module.less';
import { openBatchOptimizeModal } from './batchOptimizeModal';

type VisibilityField = 'totalVisibility' | 'doubaoVisibility' | 'deepseekVisibility' | 'yuanbaoVisibility';
type SortableField = VisibilityField | 'latestArticlePublishTime' | 'latestTaskTime';

type VisibilityRange = {
  min: number;
  max: number;
};

type TableParams = {
  pageIndex: number;
  pageSize: number;
  companyName: string;
  statuses: CompanyStatus[];
  combos: string[];
  providers: string[];
  industries: string[];
  customerTypes: string[];
  totalVisibilityMin: number;
  totalVisibilityMax: number;
  doubaoVisibilityMin: number;
  doubaoVisibilityMax: number;
  deepseekVisibilityMin: number;
  deepseekVisibilityMax: number;
  yuanbaoVisibilityMin: number;
  yuanbaoVisibilityMax: number;
  sorterField?: SortableField;
  sorterOrder?: 'ascend' | 'descend';
};

type PersistedTableParams = Omit<TableParams, 'pageIndex' | 'pageSize' | 'companyName'>;

const STORAGE_KEY = 'enterprise-monitor-table-state-v2';
const DEFAULT_RANGE: VisibilityRange = { min: 0, max: 100 };
const COMPANY_STATUS_OPTIONS: { text: string; value: CompanyStatus }[] = [
  { text: '首次交付 - 交付中', value: 'DELIVERING' },
  { text: '首次交付 - 待验收', value: 'FIRST_WAIT_ACCEPTANCE' },
  { text: '首次交付 - 已超期', value: 'FIRST_OVERDUE' },
  { text: '首次交付 - 已上线', value: 'ONLINE' },
  { text: '首次交付 - 已上线+继续优化', value: 'FIRST_ONLINE_OPTIMIZING' },
  { text: '补词优化 - 交付中', value: 'SUPPLEMENT_DELIVERING' },
  { text: '补词优化 - 待验收', value: 'SUPPLEMENT_WAIT_ACCEPTANCE' },
  { text: '补词优化 - 未达标', value: 'SUPPLEMENT_NOT_STANDARD' },
  { text: '补词优化 - 已优化', value: 'SUPPLEMENT_OPTIMIZED' },
  { text: '测试', value: 'TESTING' },
  { text: '停止交付', value: 'STOPPED' },
];

const isCompanyStatus = (value: string): value is CompanyStatus =>
  COMPANY_STATUS_OPTIONS.some((item) => item.value === value);

const getDefaultParams = (): TableParams => ({
  pageIndex: 1,
  pageSize: 10,
  companyName: '',
  statuses: [],
  combos: [],
  providers: [],
  industries: [],
  customerTypes: [],
  totalVisibilityMin: 0,
  totalVisibilityMax: 100,
  doubaoVisibilityMin: 0,
  doubaoVisibilityMax: 100,
  deepseekVisibilityMin: 0,
  deepseekVisibilityMax: 100,
  yuanbaoVisibilityMin: 0,
  yuanbaoVisibilityMax: 100,
  sorterField: undefined,
  sorterOrder: undefined,
});

const getInitialParams = (): TableParams => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return getDefaultParams();
    }
    const parsed = JSON.parse(raw) as Partial<PersistedTableParams>;
    return {
      ...getDefaultParams(),
      ...parsed,
      companyName: '',
      pageIndex: 1,
      pageSize: 10,
    };
  } catch {
    return getDefaultParams();
  }
};

const getVisibilityColor = (value: number) => {
  if (value < 30) {
    return '#ff4d4f';
  }
  if (value < 60) {
    return '#fa8c16';
  }
  return '#52c41a';
};

const isVisibilityField = (field: string): field is VisibilityField =>
  ['totalVisibility', 'doubaoVisibility', 'deepseekVisibility', 'yuanbaoVisibility'].includes(field);

const isSortableField = (field: string): field is SortableField =>
  [
    'totalVisibility',
    'doubaoVisibility',
    'deepseekVisibility',
    'yuanbaoVisibility',
    'latestArticlePublishTime',
    'latestTaskTime',
  ].includes(field);

const toSelectOptions = (values?: string[]) => (values || []).map((value) => ({ label: value, value }));

const getRangeFromParams = (params: TableParams, field: VisibilityField): VisibilityRange => {
  switch (field) {
    case 'totalVisibility':
      return { min: params.totalVisibilityMin, max: params.totalVisibilityMax };
    case 'doubaoVisibility':
      return { min: params.doubaoVisibilityMin, max: params.doubaoVisibilityMax };
    case 'deepseekVisibility':
      return { min: params.deepseekVisibilityMin, max: params.deepseekVisibilityMax };
    case 'yuanbaoVisibility':
      return { min: params.yuanbaoVisibilityMin, max: params.yuanbaoVisibilityMax };
  }
};

const buildRequest = (params: TableParams): CompanyMonitorPageReq => {
  const orderItems: OrderItem[] =
    params.sorterField && params.sorterOrder
      ? [
          {
            field: params.sorterField,
            type: params.sorterOrder === 'ascend' ? 'ASC' : 'DESC',
          },
        ]
      : [];

  return {
    pageIndex: params.pageIndex,
    pageSize: params.pageSize,
    companyName: params.companyName || undefined,
    statuses: params.statuses.length ? params.statuses : undefined,
    combos: params.combos.length ? params.combos : undefined,
    providers: params.providers.length ? params.providers : undefined,
    industries: params.industries.length ? params.industries : undefined,
    customerTypes: params.customerTypes.length ? params.customerTypes : undefined,
    totalVisibilityMin: params.totalVisibilityMin,
    totalVisibilityMax: params.totalVisibilityMax,
    doubaoVisibilityMin: params.doubaoVisibilityMin,
    doubaoVisibilityMax: params.doubaoVisibilityMax,
    deepseekVisibilityMin: params.deepseekVisibilityMin,
    deepseekVisibilityMax: params.deepseekVisibilityMax,
    yuanbaoVisibilityMin: params.yuanbaoVisibilityMin,
    yuanbaoVisibilityMax: params.yuanbaoVisibilityMax,
    orderItems,
  };
};

const OptionFilterDropdown = ({
  value,
  options,
  placeholder,
  onConfirm,
  confirm,
  clearFilters,
}: {
  value: string[];
  options: { label: string; value: string }[];
  placeholder: string;
  onConfirm: (value: string[]) => void;
} & Pick<FilterDropdownProps, 'confirm' | 'clearFilters'>) => {
  const [tempValue, setTempValue] = useState<string[]>(value);

  useEffect(() => {
    setTempValue(value);
  }, [value]);

  return (
    <div style={{ padding: 8, width: 260 }}>
      <Flex vertical gap={8}>
        <Select
          mode="multiple"
          allowClear
          showSearch
          optionFilterProp="label"
          maxTagCount="responsive"
          placeholder={placeholder}
          value={tempValue}
          options={options}
          onChange={setTempValue}
          style={{ width: '100%' }}
        />
        <Space>
          <Button
            type="primary"
            size="small"
            onClick={() => {
              onConfirm(tempValue);
              confirm();
            }}
          >
            确定
          </Button>
          <Button
            size="small"
            onClick={() => {
              setTempValue([]);
              onConfirm([]);
              clearFilters?.();
            }}
          >
            重置
          </Button>
        </Space>
      </Flex>
    </div>
  );
};

const VisibilityFilterDropdown = ({
  min,
  max,
  onConfirm,
  onReset,
  confirm,
  clearFilters,
}: {
  min: number;
  max: number;
  onConfirm: (range: VisibilityRange) => void;
  onReset: () => void;
} & Pick<FilterDropdownProps, 'confirm' | 'clearFilters'>) => {
  const [tempMin, setTempMin] = useState(min);
  const [tempMax, setTempMax] = useState(max);

  useEffect(() => {
    setTempMin(min);
    setTempMax(max);
  }, [min, max]);

  return (
    <div style={{ padding: 8 }}>
      <Flex vertical gap={8}>
        <Space>
          <InputNumber
            placeholder="最低可见度"
            min={0}
            max={100}
            value={tempMin}
            onChange={(value) => setTempMin(Number(value ?? 0))}
            style={{ width: 120 }}
          />
          <span>-</span>
          <InputNumber
            placeholder="最高可见度"
            min={0}
            max={100}
            value={tempMax}
            onChange={(value) => setTempMax(Number(value ?? 100))}
            style={{ width: 120 }}
          />
        </Space>
        <Space>
          <Button
            type="primary"
            size="small"
            onClick={() => {
              onConfirm({ min: tempMin, max: tempMax });
              confirm();
            }}
          >
            确定
          </Button>
          <Button
            size="small"
            onClick={() => {
              setTempMin(0);
              setTempMax(100);
              onReset();
              clearFilters?.();
            }}
          >
            重置
          </Button>
        </Space>
      </Flex>
    </div>
  );
};

const EnterpriseMonitorPage = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSetState<TableParams>(getInitialParams());
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedCompaniesMap, setSelectedCompaniesMap] = useState<
    Record<string, Pick<CompanyMonitorPageRow, 'companyId' | 'companyName'>>
  >({});

  useEffect(() => {
    const { pageIndex: _pageIndex, pageSize: _pageSize, companyName: _companyName, ...persistedParams } = params;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(persistedParams));
  }, [params]);

  const { data, loading } = useRequest(
    () => {
      return companyApi.dashboardPage(buildRequest(params));
    },
    {
      refreshDeps: [params],
    }
  );
  const { data: fieldOptions } = useRequest(() => companyApi.fieldOptions());
  const comboOptions = toSelectOptions(fieldOptions?.combos);
  const providerOptions = toSelectOptions(fieldOptions?.providers);
  const industryOptions = toSelectOptions(fieldOptions?.industries);
  const customerTypeOptions = toSelectOptions(fieldOptions?.customerTypes);

  const setVisibilityRange = (field: VisibilityField, range: VisibilityRange) => {
    const min = Math.max(0, Math.min(range.min, 100));
    const max = Math.max(0, Math.min(range.max, 100));

    if (field === 'totalVisibility') {
      setParams({ pageIndex: 1, totalVisibilityMin: min, totalVisibilityMax: max });
      return;
    }
    if (field === 'doubaoVisibility') {
      setParams({ pageIndex: 1, doubaoVisibilityMin: min, doubaoVisibilityMax: max });
      return;
    }
    if (field === 'deepseekVisibility') {
      setParams({ pageIndex: 1, deepseekVisibilityMin: min, deepseekVisibilityMax: max });
      return;
    }
    setParams({ pageIndex: 1, yuanbaoVisibilityMin: min, yuanbaoVisibilityMax: max });
  };

  const resetAllFilters = () => {
    const defaults = getDefaultParams();
    setParams({
      ...defaults,
      pageSize: params.pageSize,
    });
  };

  const visibilityColumn = (
    title: string,
    field: VisibilityField,
    width = 150,
    useColor = false
  ): ColumnsType<CompanyMonitorPageRow>[number] => {
    const range = getRangeFromParams(params, field);
    return {
      title,
      dataIndex: field,
      width,
      sorter: true,
      sortOrder: params.sorterField === field ? params.sorterOrder : null,
      filtered: range.min !== DEFAULT_RANGE.min || range.max !== DEFAULT_RANGE.max,
      filterDropdown: ({ confirm, clearFilters }) => (
        <VisibilityFilterDropdown
          min={range.min}
          max={range.max}
          confirm={confirm}
          clearFilters={clearFilters}
          onConfirm={(nextRange) => {
            setVisibilityRange(field, nextRange);
          }}
          onReset={() => {
            setVisibilityRange(field, { ...DEFAULT_RANGE });
          }}
        />
      ),
      render: (value: number) =>
        useColor ? (
          <Typography.Text style={{ color: getVisibilityColor(value) }}>{value}%</Typography.Text>
        ) : (
          `${value}%`
        ),
    };
  };

  const optionColumn = (
    title: string,
    field: 'customerType' | 'combo' | 'industry' | 'provider',
    value: string[],
    options: { label: string; value: string }[],
    placeholder: string,
    onConfirm: (nextValue: string[]) => void,
    width = 140
  ): ColumnsType<CompanyMonitorPageRow>[number] => ({
    title,
    dataIndex: field,
    width,
    filtered: value.length > 0,
    filterDropdown: ({ confirm, clearFilters }) => (
      <OptionFilterDropdown
        value={value}
        options={options}
        placeholder={placeholder}
        confirm={confirm}
        clearFilters={clearFilters}
        onConfirm={onConfirm}
      />
    ),
    render: (text?: string | null) => text || '-',
  });

  const columns: ColumnsType<CompanyMonitorPageRow> = [
    {
      title: '企业名称',
      dataIndex: 'companyName',
      fixed: 'left',
      width: 220,
      ellipsis: true,
      render: (value, record) => (
        <a
          onClick={() => {
            navigate(`/company?companyId=${record.companyId}`);
          }}
        >
          {value}
        </a>
      ),
    },
    visibilityColumn('整体可见度', 'totalVisibility', 160, true),
    visibilityColumn('豆包可见度', 'doubaoVisibility'),
    visibilityColumn('DS可见度', 'deepseekVisibility', 165),
    visibilityColumn('元宝可见度', 'yuanbaoVisibility'),
    {
      title: '最近一次任务时间',
      dataIndex: 'latestTaskTime',
      sorter: true,
      sortOrder: params.sorterField === 'latestTaskTime' ? params.sorterOrder : null,
      width: 200,
      render: (value: string | null) => (value ? new Date(value).toLocaleString() : '-'),
    },
    optionColumn(
      '客户类型',
      'customerType',
      params.customerTypes,
      customerTypeOptions,
      '筛选客户类型',
      (value) => setParams({ pageIndex: 1, customerTypes: value })
    ),
    optionColumn('套餐类型', 'combo', params.combos, comboOptions, '筛选套餐类型', (value) =>
      setParams({ pageIndex: 1, combos: value })
    ),
    optionColumn('行业', 'industry', params.industries, industryOptions, '筛选行业', (value) =>
      setParams({ pageIndex: 1, industries: value })
    ),
    optionColumn('服务商', 'provider', params.providers, providerOptions, '筛选服务商', (value) =>
      setParams({ pageIndex: 1, providers: value })
    ),
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      filters: COMPANY_STATUS_OPTIONS,
      filterMultiple: true,
      filteredValue: params.statuses.length ? params.statuses : null,
      width: 180,
      render: (_value, record) => record.statusDesc,
    },
    {
      title: '最新文章发布时间',
      dataIndex: 'latestArticlePublishTime',
      sorter: true,
      sortOrder: params.sorterField === 'latestArticlePublishTime' ? params.sorterOrder : null,
      width: 220,
      render: (value: string | null) => (value ? new Date(value).toLocaleString() : '-'),
    },
    {
      title: '操作',
      width: 130,
      render: (_, record) => (
        <Button
          type="link"
          onClick={() => {
            openBatchOptimizeModal({
              selectedCompanies: [{ companyId: record.companyId, companyName: record.companyName }],
              allowMultiSelect: false,
            });
          }}
        >
          开始优化
        </Button>
      ),
    },
  ];

  const selectedCompanies = selectedRowKeys
    .map((key) => selectedCompaniesMap[String(key)])
    .filter((item): item is Pick<CompanyMonitorPageRow, 'companyId' | 'companyName'> => Boolean(item));

  return (
    <div className={styles.page}>
      <div className={styles.toolbar}>
        <div className={styles.toolbarRight}>
          {selectedRowKeys.length > 0 && (
            <>
              <Button
                onClick={() => {
                  setSelectedRowKeys([]);
                  setSelectedCompaniesMap({});
                }}
              >
                取消选择({selectedRowKeys.length})
              </Button>
              <Button
                type="primary"
                onClick={() => {
                  openBatchOptimizeModal({
                    selectedCompanies,
                    allowMultiSelect: true,
                  });
                }}
              >
                批量开始优化({selectedRowKeys.length})
              </Button>
            </>
          )}
          <Button type="primary" onClick={resetAllFilters} style={{ marginLeft: 'auto' }}>
            重置筛选
          </Button>
          <SearchInput
            defaultValue={params.companyName}
            onSearch={(value) => {
              setParams({ pageIndex: 1, companyName: value.trim() });
            }}
            style={{ width: 240 }}
            placeholder="请输入企业名称"
          />
        </div>
      </div>

      <div className={styles.tableContainer}>
        <CommonTable<CompanyMonitorPageRow>
          loading={loading}
          rowKey="companyId"
          columns={columns}
          dataSource={data?.records || []}
          total={data?.total || 0}
          params={params}
          setParams={setParams}
          rowSelection={{
            preserveSelectedRowKeys: true,
            selectedRowKeys,
            onChange: (nextSelectedRowKeys, nextSelectedRows) => {
              setSelectedRowKeys(nextSelectedRowKeys);
              setSelectedCompaniesMap((prev) => {
                const nextMap: Record<string, Pick<CompanyMonitorPageRow, 'companyId' | 'companyName'>> = { ...prev };
                Object.keys(nextMap).forEach((companyId) => {
                  if (!nextSelectedRowKeys.includes(companyId)) {
                    delete nextMap[companyId];
                  }
                });
                nextSelectedRows.forEach((row) => {
                  nextMap[row.companyId] = {
                    companyId: row.companyId,
                    companyName: row.companyName,
                  };
                });
                (data?.records || []).forEach((row) => {
                  if (nextSelectedRowKeys.includes(row.companyId)) {
                    nextMap[row.companyId] = {
                      companyId: row.companyId,
                      companyName: row.companyName,
                    };
                  }
                });
                return nextMap;
              });
            },
          }}
          onChange={(pagination: TablePaginationConfig, _filters, sorter) => {
            const statusRawFilters =
              (_filters.status as React.Key[] | null) || [];
            const statusFilters = statusRawFilters
              .map((item) => String(item))
              .filter((item): item is CompanyStatus => isCompanyStatus(item));
            const normalizedSorter = (
              Array.isArray(sorter) ? sorter[0] : sorter
            ) as SorterResult<CompanyMonitorPageRow>;
            const sorterField = typeof normalizedSorter.field === 'string' ? normalizedSorter.field : '';

            setParams({
              pageIndex: pagination.current || 1,
              pageSize: pagination.pageSize || params.pageSize,
              statuses: statusFilters,
              sorterField: sorterField && isSortableField(sorterField) ? sorterField : undefined,
              sorterOrder: normalizedSorter.order || undefined,
            });
          }}
        />
      </div>
    </div>
  );
};

export default EnterpriseMonitorPage;
